/**
 * Typed HTTP helpers for Widget Edge Gateway:
 * GET /v1/config/{pk}, POST /v1/sessions
 */

import { appConfig, log } from '../config/env';
import type {
  CreateSessionRequest,
  SessionIdentity,
  SessionResponse,
  WidgetConfig,
} from '../types/api';
import type { WidgetChannel } from '../types/bridge';
import { parseSessionResponse, parseWidgetConfig } from './apiGuards';
import { embedGatewayHeaders } from './configHelpers';
import { WidgetApiError } from './errors';
import { isRecord } from './typeGuards';

export { WidgetApiError } from './errors';

/** Map host identify payload → Gateway `identity` body fields. */
export function toSessionIdentity(identity: {
  userId: string;
  hmac: string;
}): SessionIdentity {
  return {
    user_id: identity.userId.trim(),
    user_hash: identity.hmac.trim(),
  };
}

function apiUrl(path: string): string {
  return `${appConfig.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

async function parseJsonBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new WidgetApiError(
      'invalid_json',
      `Unexpected non-JSON response (${response.status})`,
      response.status,
    );
  }
}

async function readErrorBody(response: Response, body: unknown): Promise<never> {
  const record = isRecord(body) ? body : null;
  const code =
    record && typeof record.error === 'string' ? record.error : 'http_error';
  const message =
    record && typeof record.message === 'string'
      ? record.message
      : `Request failed (${response.status})`;
  throw new WidgetApiError(code, message, response.status);
}

export async function fetchWidgetConfig(
  publishableKey: string,
  options?: {
    signal?: AbortSignal;
    channel?: WidgetChannel;
    bundleId?: string;
  },
): Promise<WidgetConfig> {
  const channel = options?.channel ?? 'web_widget';
  const url = apiUrl(`/v1/config/${encodeURIComponent(publishableKey)}`);
  log('debug', 'GET widget config', { url, channel });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Channel: channel,
      ...embedGatewayHeaders({
        channel,
        bundleId: options?.bundleId,
      }),
    },
    signal: options?.signal,
  });

  const body = await parseJsonBody(response);
  if (!response.ok) await readErrorBody(response, body);
  return parseWidgetConfig(body);
}

export async function createSession(
  request: CreateSessionRequest,
  signal?: AbortSignal,
): Promise<SessionResponse> {
  const url = apiUrl('/v1/sessions');
  const channel = request.surface ?? 'web_widget';
  log('debug', 'POST widget session', {
    url,
    surface: channel,
    hasVisitor: Boolean(request.visitor_id),
    hasBundle: Boolean(request.app_bundle_id),
    hasIdentity: Boolean(request.identity?.user_id && request.identity?.user_hash),
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Channel: channel,
      ...embedGatewayHeaders({
        channel,
        bundleId: request.app_bundle_id,
      }),
    },
    body: JSON.stringify(request),
    signal,
  });

  const body = await parseJsonBody(response);
  if (!response.ok) await readErrorBody(response, body);
  return parseSessionResponse(body);
}

/** In-memory session with expiry skew for proactive refresh. */
export interface ActiveSession {
  accessToken: string;
  expiresAt: number;
  sessionId: string;
  visitorId: string;
  avatarId: string;
}

const EXPIRY_SKEW_MS = 30_000;

export function sessionFromResponse(res: SessionResponse): ActiveSession {
  return {
    accessToken: res.access_token,
    expiresAt: Date.now() + Math.max(0, res.expires_in) * 1000,
    sessionId: res.session_id,
    visitorId: res.visitor_id,
    avatarId: res.avatar_id,
  };
}

export function isSessionValid(session: ActiveSession | null): boolean {
  if (!session?.accessToken) return false;
  return Date.now() < session.expiresAt - EXPIRY_SKEW_MS;
}

export interface EnsureSessionOptions {
  publishableKey: string;
  channel: WidgetChannel;
  visitorId?: string;
  current?: ActiveSession | null;
  /** Skip reuse and always POST /v1/sessions (e.g. header reset). */
  force?: boolean;
  signal?: AbortSignal;
  /** Native SDK `?bundle=` — forwarded as `app_bundle_id` (proposal §5.6). */
  bundleId?: string;
  /**
   * Host identify payload. Forwarded as `identity: { user_id, user_hash }`
   * when present (required by Gateway if identity_verification is enabled).
   */
  identity?: { userId: string; hmac: string } | null;
}

export async function ensureSession(
  options: EnsureSessionOptions,
): Promise<ActiveSession> {
  const current = options.current ?? null;
  if (!options.force && current && isSessionValid(current)) {
    return current;
  }

  const identity =
    options.identity?.userId && options.identity?.hmac
      ? toSessionIdentity(options.identity)
      : undefined;

  const res = await createSession(
    {
      publishable_key: options.publishableKey,
      visitor_id: options.visitorId || current?.visitorId,
      surface: options.channel,
      ...(options.channel === 'mobile_webview' && options.bundleId
        ? { app_bundle_id: options.bundleId }
        : {}),
      ...(identity ? { identity } : {}),
    },
    options.signal,
  );

  return sessionFromResponse(res);
}
